import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { openedStructureSetApprovalConfirmationDialogState } from '~/state/atoms/structureSet/roiAtoms';

interface ApprovalStatusDropdownProps {
    structureSet: StructureSetReadModel;
}

export default function ApprovalStatusDropdown({
    structureSet,
}: ApprovalStatusDropdownProps) {
    const setOpenedStructureSetApprovalConfirmationDialog = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );

    const handleChange = (e: SelectChangeEvent<DICOMApprovalStatus>) => {
        const newApprovalStatus = e.target.value as DICOMApprovalStatus;
        if (newApprovalStatus !== structureSet.approvalStatus) {
            setOpenedStructureSetApprovalConfirmationDialog({
                sopUid: structureSet?.dicomUid,
                newApprovalStatus: newApprovalStatus,
            });
        }
    };

    const allStatuses = Object.values(DICOMApprovalStatus);
    const currentValue = allStatuses.includes(structureSet.approvalStatus)
        ? structureSet.approvalStatus
        : DICOMApprovalStatus.UNAPPROVED;

    return (
        <FormControl fullWidth size="small" sx={{ position: 'relative' }}>
            <InputLabel id="approval-status-label" shrink>
                <FormattedMessage
                    id="approvalStatus"
                    defaultMessage="Approval Status"
                />
            </InputLabel>

            <Select
                labelId="approval-status-label"
                label={
                    <FormattedMessage
                        id="approvalStatus"
                        defaultMessage="Approval Status"
                    />
                }
                value={currentValue}
                data-testid="approval-status-dropdown"
                onChange={handleChange}
                renderValue={(selected) => (
                    <ReviewStatus status={selected} fontSize={14} />
                )}
            >
                {Object.values(DICOMApprovalStatus)
                    .filter((status) => status !== structureSet.approvalStatus)
                    .map((status) => (
                        <MenuItem
                            key={status}
                            value={status}
                            sx={{ fontSize: '0.875rem' }}
                            data-testid={`approval-status-option-${status.toLowerCase()}`}
                        >
                            <ReviewStatus status={status} fontSize={14} />
                        </MenuItem>
                    ))}
            </Select>
        </FormControl>
    );
}
